import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { m } from 'motion/react';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import { Button } from '@/design/components/Button';
import { LogoDisc } from '@/design/components/Logo';
import { cx } from '@/lib/cx';

const KEY = 'dec.tour.done.v1';

export function tourSeen(userId: string): boolean {
  try {
    return localStorage.getItem(`${KEY}:${userId}`) === '1';
  } catch {
    return true;
  }
}

function markSeen(userId: string) {
  try {
    localStorage.setItem(`${KEY}:${userId}`, '1');
  } catch {
    // Private mode: the tour simply shows again next time.
  }
}

interface Step {
  /** `data-tour` of the element to highlight; none (or not on screen) shows the card centred. */
  target?: string;
  title: string;
  text: string;
}

const STEPS: Step[] = [
  {
    title: '¡Hola! Te muestro cómo funciona',
    text: 'Son 7 pasos cortitos. Lo podés volver a ver cuando quieras desde el botón "Ayuda" de arriba.',
  },
  {
    target: 'flow',
    title: 'Así trabajamos',
    text: 'Todo sigue el mismo camino: alguien consulta, le armamos un presupuesto, si dice que sí es un trabajo, lo fabricamos, cobramos y entregamos.',
  },
  {
    target: 'next',
    title: 'Lo que tenés que hacer hoy',
    text: 'Acá aparece lo que espera por vos, lo más urgente primero. Tocá una fila y te lleva justo donde tenés que actuar.',
  },
  {
    target: 'new-quote',
    title: 'El botón dorado',
    text: 'En cada pantalla hay un solo botón dorado: es lo próximo que conviene hacer. Si no sabés qué tocar, tocá el dorado.',
  },
  {
    target: 'nav',
    title: 'El menú',
    text: 'Están todas las partes del sistema, ordenadas: vender, fabricar y entregar, comprar y la plata. Pasá el mouse por cada una y te dice para qué sirve.',
  },
  {
    target: 'search',
    title: 'Buscar cualquier cosa',
    text: 'Escribí el nombre de un cliente, un número de presupuesto o de trabajo y llegás directo.',
  },
  {
    target: 'help',
    title: 'Si te perdés, Ayuda',
    text: 'En cada pantalla, "Ayuda" te explica qué es, qué podés hacer y paso a paso. Y las palabras subrayadas con puntos se explican solas al tocarlas.',
  },
];

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 8;

/** A first-run walk through the screen: dims everything except the highlighted part and explains it. */
export function WelcomeTour({ userId, onClose }: { userId: string; onClose: () => void }) {
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState<Rect | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const step = STEPS[index]!;
  const last = index === STEPS.length - 1;

  const finish = useCallback(() => {
    markSeen(userId);
    onClose();
  }, [userId, onClose]);

  // Follow the highlighted element (it may scroll or the window may resize).
  useLayoutEffect(() => {
    const el = visibleTarget(step.target);
    const find = () => {
      const r = el?.getBoundingClientRect();
      if (!r || r.width === 0 || r.height === 0) return setRect(null);
      setRect({ top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 });
    };
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) el.scrollIntoView({ block: 'center', behavior: 'instant' as ScrollBehavior });
    }
    find();
    window.addEventListener('resize', find);
    window.addEventListener('scroll', find, true);
    return () => {
      window.removeEventListener('resize', find);
      window.removeEventListener('scroll', find, true);
    };
  }, [step.target]);

  useEffect(() => {
    cardRef.current?.querySelector<HTMLButtonElement>('[data-primary]')?.focus();
  }, [index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') setIndex((i) => Math.min(i + 1, STEPS.length - 1));
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(i - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [finish]);

  const card = cardPosition(rect);

  return createPortal(
    <div className="fixed inset-0 z-[80]" role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-describedby="tour-text">
      {/* Dimmed backdrop with a hole over the highlighted part. */}
      {rect ? (
        <m.div
          className="pointer-events-none fixed rounded-2xl border-2 border-gold-400"
          initial={false}
          animate={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
          transition={{ type: 'spring', stiffness: 320, damping: 34 }}
          style={{ boxShadow: '0 0 0 9999px rgb(28 26 23 / 0.55)' }}
        />
      ) : (
        <div className="fixed inset-0 bg-ink-900/55" />
      )}
      {/* Clicks outside the card don't reach the page underneath. */}
      <div className="fixed inset-0" onClick={(e) => e.stopPropagation()} aria-hidden />

      <m.div
        ref={cardRef}
        key={index}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className={cx('fixed w-[min(380px,calc(100vw-24px))] rounded-2xl border border-gold-200 bg-surface-0 p-5 shadow-e4', !card && 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2')}
        style={card ?? undefined}
      >
        <div className="gold-hairline absolute inset-x-8 top-0" />
        <div className="flex items-start gap-3">
          {index === 0 && <LogoDisc size={44} className="shrink-0" />}
          <div className="min-w-0 flex-1">
            <div className="overline !text-gold-700">
              Paso {index + 1} de {STEPS.length}
            </div>
            <h2 id="tour-title" className="font-display mt-0.5 text-[23px] leading-tight text-ink-900">
              {step.title}
            </h2>
          </div>
          <button type="button" onClick={finish} className="-mt-1 -mr-1 rounded-lg p-1.5 text-ink-400 hover:bg-surface-2 hover:text-ink-900" aria-label="Cerrar el recorrido">
            <X className="size-4" />
          </button>
        </div>
        <p id="tour-text" className="mt-2 text-[14.5px] leading-relaxed text-ink-700">
          {step.text}
        </p>
        <div className="mt-4 flex items-center gap-1.5" aria-hidden>
          {STEPS.map((_, i) => (
            <span key={i} className={cx('h-1.5 rounded-full transition-all', i === index ? 'w-5 bg-gold-500' : i < index ? 'w-1.5 bg-gold-300' : 'w-1.5 bg-line-strong')} />
          ))}
        </div>
        <div className="mt-4 flex items-center gap-2">
          {index === 0 ? (
            <Button variant="ghost" size="sm" onClick={finish}>
              Ahora no
            </Button>
          ) : (
            <Button variant="ghost" size="sm" icon={<ArrowLeft className="size-3.5" />} onClick={() => setIndex(index - 1)}>
              Anterior
            </Button>
          )}
          <div className="flex-1" />
          <Button
            data-primary
            variant="gold"
            iconRight={last ? <Check className="size-4" /> : <ArrowRight className="size-4" />}
            onClick={() => (last ? finish() : setIndex(index + 1))}
          >
            {index === 0 ? 'Empezar' : last ? '¡Listo!' : 'Siguiente'}
          </Button>
        </div>
      </m.div>
    </div>,
    document.body,
  );
}

/** The first element with that `data-tour` that is actually on screen (the sidebar is hidden on phones). */
function visibleTarget(target: string | undefined): HTMLElement | null {
  if (!target) return null;
  for (const el of document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`)) {
    const r = el.getBoundingClientRect();
    if (r.width > 0 && r.height > 0) return el;
  }
  return null;
}

/** Puts the card next to the highlight: below if there's room, else above, else beside it. */
function cardPosition(rect: Rect | null): CSSProperties | null {
  if (!rect) return null;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(380, vw - 24);
  const h = 260;
  const clampX = (x: number) => Math.max(12, Math.min(x, vw - w - 12));
  const clampY = (y: number) => Math.max(12, Math.min(y, vh - h - 12));
  if (rect.top + rect.height + 12 + h < vh) return { top: rect.top + rect.height + 12, left: clampX(rect.left) };
  if (rect.top - 12 - h > 0) return { bottom: vh - rect.top + 12, left: clampX(rect.left) };
  if (rect.left + rect.width + 12 + w < vw) return { top: clampY(rect.top), left: rect.left + rect.width + 12 };
  if (rect.left - 12 - w > 0) return { top: clampY(rect.top), left: rect.left - 12 - w };
  return null;
}
